import React, { useState } from 'react';
import { Balloon, Ul, Li, MultiModal, useMutation, reduxForm, useQuery, ASYNC_TASK_FRAGMENT } from '../components';
import { gql } from '@apollo/client';
import styles from './ChangeSurveyInformation.scss';
import { TextField, SelectField, DateField, CheckboxField } from 'jbc-front/components/Form';
import { useNotify } from '../actions';
import { dateString, futureDate, maxLength } from '../validators';
import moment from 'moment';
import Button from 'jbc-front/components/Button';
import { Pulldown } from 'jbc-front/components/icons';
import useReactRouter from 'use-react-router';

const REQUEST_OPTION = gql`
  query request_option($mailType: Int!) {
    client {
      id
      mails(mailType: $mailType) {
        id
        name
        title
        content
        updatedAt
      }
      asyncTask {
        ...AsyncTaskFields
      }
    }
  }
  ${ASYNC_TASK_FRAGMENT}
`;

const CHANGE_DEADLINE_REQUEST = gql`
  mutation change_deadline($input: ChangeDeadlineInput!) {
    changeDeadline(input: $input) {
      client {
        implementationSurveys {
          id
          surveyEndDate
        }
      }
    }
  }
`;

const CHANGE_SURVEY_TITLE_REQUEST = gql`
  mutation change_survey_title($input: ChangeSurveyTitleInput!) {
    changeSurveyTitle(input: $input) {
      client {
        implementationSurveys {
          id
          title
        }
      }
    }
  }
`;

const isDateString = date => {
  if (!date || !date.match(/^\d{4}\/\d{1,2}\/\d{1,2}$/)) return false;
  return true;
};

const isFutureDate = date => {
  return date && moment(date).endOf('days').isAfter(moment());
};

const ChangeDeadlineModal = ({
  selectedSurvey,
  targetCount,
  onUpdated,
  displayType,
  hideModal,
  bodyRef,
  isTaskRunning,
  mails,
  requestOptions
}) => {
  const [surveyEndDate, setSurveyEndDate] = useState(null);
  const { history } = useReactRouter();

  const mailOptions = mails.map(mail => {
    return {
      label: mail.name,
      value: mail.id
    };
  });

  const [selectedEmail, setSelectedEmail] = useState(mailOptions[0].value);
  const [isSendMail, setIsSendMail] = useState(false);

  const {
    mutation,
    value: { loading }
  } = useMutation(CHANGE_DEADLINE_REQUEST);

  const notify = useNotify();

  const changeDeadline = async () => {
    if (isDateString(surveyEndDate) && isFutureDate(surveyEndDate) && selectedEmail) {
      await mutation({
        variables: {
          input: {
            implementationSurveyId: parseInt(selectedSurvey.id, 10),
            mailId: parseInt(selectedEmail, 10),
            surveyEndDate: surveyEndDate,
            isSendMail: isSendMail
          }
        }
      });
      hideModal();
      await requestOptions.refetch();
      onUpdated();

      if (displayType === 'finishedSurveys' && targetCount > 0) {
        history.push(`/dashboard/unfinishedSurveys/${selectedSurvey.id}`);
      } else {
        history.push(`/dashboard/${displayType}/${selectedSurvey.id}`);
      }
    } else if (!isDateString(surveyEndDate)) {
      notify('実施期限の入力形式が正しくありません', 'error');
    } else if (!isFutureDate(surveyEndDate)) {
      notify('実施期限には未来の日付を入れてください', 'error');
    } else {
      notify('メール内容を選択してください', 'error');
    }
  };

  return (
    <MultiModal.Modal type="change_deadline">
      <MultiModal.Header hideModal={hideModal}>実施期間の変更</MultiModal.Header>
      <MultiModal.Body ref={bodyRef}>
        <div className={styles.modalBody}>
          <div>ストレスチェックの実施期間を変更します。</div>
          <div className={styles.dateField}>
            <div className={styles.selectDate}>
              <DateField
                name="surveyEndDate"
                label="実施期間"
                onChange={(_, value) => setSurveyEndDate(value)}
                validate={[dateString, futureDate]}
                required
              />
            </div>
            <div className={styles.endText}>まで</div>
          </div>

          {targetCount > 0 && (
            <>
              <div className={styles.checkbox}>
                <CheckboxField
                  onChange={() => setIsSendMail(!isSendMail)}
                  name="isSendMail"
                  label="変更時に通知メールも送信"
                />
              </div>
              <div className={styles.checkboxText}>通知メールは未回答者にのみ送信されます。</div>
            </>
          )}

          {isSendMail && targetCount > 0 && (
            <div className={styles.mailField}>
              <div className={styles.sendCount}>送信人数：{targetCount}人（未回答者）</div>
              <div className={styles.selectMail}>
                <SelectField
                  input={{
                    value: selectedEmail,
                    onChange: value => {
                      setSelectedEmail(value);
                    }
                  }}
                  label="メール内容"
                  name="Mail"
                  simpleValue
                  required
                  clearable={false}
                  options={mailOptions}
                />
              </div>
            </div>
          )}
        </div>
      </MultiModal.Body>
      <MultiModal.Footer>
        <div className={styles.modalFooter}>
          <MultiModal.Buttons>
            <Button className={styles.cancelButton} onClick={hideModal}>
              キャンセル
            </Button>
            <Button
              className={styles.sendButton}
              primary
              onClick={changeDeadline}
              disabledReason={isTaskRunning ? 'ストレスチェック送信中のため送信できません' : null}
              disabled={
                !selectedEmail ||
                loading ||
                isTaskRunning ||
                !isDateString(surveyEndDate) ||
                !isFutureDate(surveyEndDate)
              }>
              変更
            </Button>
          </MultiModal.Buttons>
        </div>
      </MultiModal.Footer>
    </MultiModal.Modal>
  );
};

const ChangeSurveyTitleForm = reduxForm({
  form: 'ChangeSurveyTitleForm'
})(({ submitting, handleSubmit, hideModal, bodyRef, isTaskRunning, invalid }) => {
  const isDisabled = submitting || isTaskRunning || invalid;
  return (
    <MultiModal.Modal type="change_survey_title">
      <MultiModal.Header hideModal={hideModal}>フォーム名称の変更</MultiModal.Header>
      <MultiModal.Body ref={bodyRef}>
        <div className={styles.modalBody}>
          <div>フォーム名称を変更します。</div>
          <div className={styles.titleField}>
            <TextField name="surveyTitle" label="フォーム名称" validate={maxLength(50)} required />
          </div>
        </div>
      </MultiModal.Body>
      <MultiModal.Footer>
        <div className={styles.modalFooter}>
          <MultiModal.Buttons>
            <Button className={styles.cancelButton} onClick={hideModal}>
              キャンセル
            </Button>
            <Button
              className={styles.sendButton}
              primary
              onClick={handleSubmit}
              disabledReason={isTaskRunning ? 'ストレスチェック送信中のため送信できません' : null}
              disabled={isDisabled}>
              変更
            </Button>
          </MultiModal.Buttons>
        </div>
      </MultiModal.Footer>
    </MultiModal.Modal>
  );
});

const ChangeSurveyTitleModal = ({ selectedSurvey, onUpdated, hideModal, bodyRef, isTaskRunning }) => {
  const { mutation } = useMutation(CHANGE_SURVEY_TITLE_REQUEST);

  const changeSurveyTitle = async values => {
    await mutation({
      variables: {
        input: {
          implementationSurveyId: parseInt(selectedSurvey.id, 10),
          title: values.surveyTitle
        }
      }
    });
    hideModal();
    onUpdated();
  };

  return (
    <ChangeSurveyTitleForm
      onSubmit={changeSurveyTitle}
      initialValues={{ surveyTitle: selectedSurvey?.title }}
      selectedSurvey={selectedSurvey}
      onUpdated={onUpdated}
      hideModal={hideModal}
      bodyRef={bodyRef}
      isTaskRunning={isTaskRunning}
    />
  );
};

const ChangeSurveyInformation = ({ selectedSurvey, targetCount, disabled, onUpdated, displayType }) => {
  const requestOptions = useQuery(REQUEST_OPTION, {
    variables: { mailType: 1 },
    fetchPolicy: 'network-only'
  });

  if (requestOptions.loading) {
    return (
      <Button className={styles.showModal} disabled={true}>
        調査フォームの情報を変更する
        <span className={styles.triangle} />
      </Button>
    );
  }

  const isTaskRunning = ['waiting', 'in_progress'].includes(requestOptions.data.client.asyncTask?.status);
  const mails = requestOptions.data.client.mails;

  return (
    <MultiModal>
      {({ hideModal, showModal, showType }) => {
        const ref = React.createRef<any>();
        return (
          <div className={styles.balloon}>
            <Balloon
              right={true}
              center={false}
              switchRender={(toggle, _) => (
                <Button
                  className={styles.showModal}
                  onClick={() => {
                    toggle();
                  }}
                  disabled={disabled}>
                  調査フォームの情報を変更する
                  <span className={styles.triangle} />
                </Button>
              )}>
              {hide => (
                <React.Fragment>
                  <Ul className={styles.ul}>
                    <Li className={styles.links}>
                      <a
                        className={styles.a}
                        onClick={() => {
                          hide();
                          showModal('change_deadline');
                        }}>
                        <Pulldown size={10} className={styles.pulldown} />
                        実施期間を変更する
                      </a>
                      <a
                        className={styles.a}
                        onClick={() => {
                          hide();
                          showModal('change_survey_title');
                        }}>
                        <Pulldown size={10} className={styles.pulldown} />
                        フォーム名称を変更する
                      </a>
                    </Li>
                  </Ul>
                </React.Fragment>
              )}
            </Balloon>
            <form>
              {showType === 'change_deadline' && (
                <ChangeDeadlineModal
                  selectedSurvey={selectedSurvey}
                  targetCount={targetCount}
                  onUpdated={onUpdated}
                  displayType={displayType}
                  hideModal={hideModal}
                  bodyRef={ref}
                  isTaskRunning={isTaskRunning}
                  mails={mails}
                  requestOptions={requestOptions}
                />
              )}
              {showType === 'change_survey_title' && (
                <ChangeSurveyTitleModal
                  selectedSurvey={selectedSurvey}
                  onUpdated={onUpdated}
                  hideModal={hideModal}
                  bodyRef={ref}
                  isTaskRunning={isTaskRunning}
                />
              )}
            </form>
          </div>
        );
      }}
    </MultiModal>
  );
};

export const ChangeSurveyInformationForm = reduxForm({
  form: 'ChangeSurveyInformationForm',
  enableReinitialize: true
})(ChangeSurveyInformation);
